import { css } from '@emotion/react';
import {
  type AnchorHTMLAttributes,
  type ElementType,
  type FC,
  type ReactNode,
} from 'react';

export type LinkTileProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
  /** Element to render the link as, such as Next.js `Link`. */
  linkAs?: ElementType;
  /** Background photo. Decorative; the title names the link. */
  image?: { src: string; alt?: string };
  title: ReactNode;
  /** Secondary line, for example "53 properties". */
  meta?: ReactNode;
  /** Use `eager` for tiles near the top of the page. Defaults to `lazy`. */
  imageLoading?: 'lazy' | 'eager';
};

const tileStyles = css`
  position: relative;
  display: flex;
  align-items: flex-end;
  min-height: 220px;
  overflow: hidden;
  border-radius: 16px;
  background: var(--surface-sand);
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.03);
  }

  &:focus-visible {
    outline: 3px solid var(--border-focus);
    outline-offset: 3px;
  }
`;

const bodyStyles = (hasImage: boolean) => css`
  position: relative;
  display: flex;
  flex: 1;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--spacing-3);
  padding: var(--spacing-12) var(--spacing-5) 18px;
  background: ${hasImage
    ? 'linear-gradient(180deg, rgba(12, 22, 15, 0) 0%, rgba(12, 22, 15, 0.85) 100%)'
    : 'none'};
  color: ${hasImage ? 'var(--color-base-white)' : 'var(--color-primary-800)'};
`;

const arrowStyles = css`
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--surface-subtle);
`;

/**
 * A photo tile that links somewhere, with a title and a short meta line.
 */
const LinkTile: FC<LinkTileProps> = ({
  href,
  linkAs: Component = 'a',
  image,
  title,
  meta,
  imageLoading = 'lazy',
  className,
  ...rest
}) => (
  <Component {...rest} href={href} className={className} css={tileStyles}>
    {image && (
      <img src={image.src} alt={image.alt ?? ''} loading={imageLoading} />
    )}
    <span css={bodyStyles(Boolean(image))}>
      <span>
        <span css={{ display: 'block', fontSize: 22, fontWeight: 800 }}>
          {title}
        </span>
        {meta && (
          <span
            css={{ display: 'block', marginTop: 2, fontSize: 14, opacity: 0.9 }}
          >
            {meta}
          </span>
        )}
      </span>
      <span aria-hidden="true" css={arrowStyles}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--color-primary-800)"
          strokeWidth="2.4"
          strokeLinecap="round"
        >
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      </span>
    </span>
  </Component>
);

LinkTile.displayName = 'LinkTile';

export default LinkTile;
